import { useEffect, useRef, useState } from "react";
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { typesOfComments } from "@/components/video/videoComments/main/constants"
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/index';
import InitialAvatar from "@/components/video/videoComments/avtar"
import EmojiPopup from "@/components/common/emojiPopup/emojiPopup"
import { maxScreenWidthForDesktop } from "@/pages/watch/main/constants"

import { handleAddVideoComment, handleAddReplyToComment, handleAddReplyToReply } from "@/utils/apiUtils"
import "./style.css"
import { useAuthToken } from "@/hooks/useAppSelectors";
import { addCurrentThemeClassToEle } from "@/utils";
import { themes } from "@/constants/colors_v2";

export default function CommentBox1(props: any) {
    const token = useAuthToken()

    const { typeOfComment, username, cancleOfParent,
        commentDetails, autoFocusOnMount, handleBlurFromParent } = props
    const dispatch = useDispatch();
    const videoDetails = useSelector((state: RootState) => state.videoReducer.video);
    const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);

    const { common } = useLocalTranslation();
    const [commentBtnIsDisable, setCommentBtnIsDisable] = useState(true)
    const [inputBoxInFocus, setInputBoxInFocus] = useState(false)
    const [comment, setComment] = useState("")

    const inputCommentRef = useRef<any>(null);

    useEffect(() => {
        if (window.innerWidth < maxScreenWidthForDesktop) {
            if (autoFocusOnMount && inputCommentRef.current) {
                inputCommentRef.current.focus();
                setInputBoxInFocus(true); // Show buttons, emoji etc.
            }
        }
    }, [autoFocusOnMount]);


    const handleFocus = () => {
        setInputBoxInFocus(true)
    }

    const handleCancle = () => {
        setInputBoxInFocus(false)
        setComment("")
        if (cancleOfParent) {
            cancleOfParent()
        }
    }

    const handleCommentChange = (event: any) => {
        const val = event.target.value
        if (val.length > 0) {
            setCommentBtnIsDisable(false)
        } else {
            setCommentBtnIsDisable(true)
        }
        setComment(val)
    }

    const submitComment = () => {
        setCommentBtnIsDisable(true)
        if (typeOfComment === typesOfComments.firstComment
            || typeOfComment === typesOfComments.notFirstComment) {
            addVideoComment()
        } else if (typeOfComment === typesOfComments.replyToComment) {
            addReplyToComment()
        } else if (typeOfComment === typesOfComments.replyToReply) {
            addReplyToReply()
        }
    }

    const addVideoComment = async () => {
        if (videoDetails) {
            let payload = {
                videoDocId: videoDetails?._id,
                comment: comment
            }
            const handleSubmitCommentStatus = await handleAddVideoComment(payload, dispatch)
        }
        setCommentBtnIsDisable(false)
        handleCancle()
    }

    const addReplyToComment = async () => {
        if (commentDetails) {
            let payload = {
                commentId: commentDetails._id,
                reply: comment
            }
            const handleSubmitCommentStatus = await handleAddReplyToComment(payload, dispatch)
        }
        setCommentBtnIsDisable(false)
        handleCancle()
    }

    const addReplyToReply = async () => {
        if (commentDetails) {
            let payload = {
                commentId: commentDetails.commentId,
                replyOnId: commentDetails._id,
                reply: comment
            }
            const handleSubmitCommentStatus = await handleAddReplyToReply(payload, dispatch)
        }
        setCommentBtnIsDisable(false)
        handleCancle()
    }



    const handleEmojiInsert = (emoji: string) => {
        if (inputCommentRef.current) {
            const { selectionStart, selectionEnd } = inputCommentRef.current;

            const newValue =
                comment.substring(0, selectionStart) +
                emoji +
                comment.substring(selectionEnd);

            const val = {
                target: {
                    value: newValue
                }
            }
            handleCommentChange(val)

            // After updating the state, we need to re-focus and set the cursor position
            // This needs to be done in a setTimeout or using useCallback/useMemo
            // because state updates are asynchronous.
            setTimeout(() => {
                if (inputCommentRef.current) {
                    const newCursorPos = selectionStart + emoji.length;
                    inputCommentRef.current.focus();
                    inputCommentRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        }
    };

    const handleBlur = () => {
        if (handleBlurFromParent) {
            handleBlurFromParent()
        }
    }

    const getClassNameForComentBtn = () => {
        if (commentBtnIsDisable) {
            if(currentTheme === themes.dark){
                return "comment_btn_disabled_dark_theme"
            } else {
                return "comment_btn_disabled_light_theme"
            }
        } else {
            if(currentTheme === themes.dark){
                return "comment_btn_not_disabled_dark_theme"
            } else {
                return "comment_btn_not_disabled_light_theme"
            }
        }
    }

    return (
        <>
            {token &&
                <div
                    className={`commentBox1 ${addCurrentThemeClassToEle(currentTheme, 'commentBox1')}`}>
                    <InitialAvatar
                        name={username}
                    />
                    <div className="commentBox1Con1">
                        <input
                            value={comment}
                            ref={inputCommentRef}
                            onChange={(event) => handleCommentChange(event)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            className="commentInputBox"
                            placeholder={(typeOfComment === typesOfComments.firstComment
                                || typeOfComment === typesOfComments.notFirstComment) ?
                                common.statusText.addAComment : common.statusText.addAReply}
                            type="text" />

                        {(inputBoxInFocus || cancleOfParent) &&
                            <div className="commentBtnsCon">
                                <div>
                                    {window.innerWidth > maxScreenWidthForDesktop &&
                                        <EmojiPopup onEmojiSelect={handleEmojiInsert} />
                                    }
                                </div>
                                <div>
                                    <button
                                        onClick={handleCancle}
                                        className="cancleBtn">
                                        {common.statusText.cancleBtn}
                                    </button>
                                    <button
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={submitComment}
                                        disabled={commentBtnIsDisable}
                                        style={{
                                            cursor: commentBtnIsDisable ? 'not-allowed' : 'pointer',
                                        }}
                                        className={`commentBtn ${getClassNameForComentBtn()}`}>
                                        {(typeOfComment === typesOfComments.firstComment
                                            || typeOfComment === typesOfComments.notFirstComment) ?
                                            common.statusText.commentBtn : common.statusText.replyBtn}
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            }
        </>
    );
}
